import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Formik } from 'formik';
import { chain, get, isEmpty, isNil } from 'lodash';
import * as Yup from 'yup';

import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import Typography from '~/components/core/Atomic/Typography';
import ApprovalRequests from '~/components/exposures/ApprovalRequests';
import { ExposureDetailedTitle } from '~/components/exposures/ExposureDetailedTitle';
import ExposureMonetaryValueTextFieldFormik from '~/components/exposures/ExposureMonetaryValueTextFieldFormik';
import AdditionalReserveVerificationDialog from '~/components/exposures/PaymentRequestContainer/AdditionalReserveVerificationDialog';
import AnomalousPaymentBanner, {
  IssueSeverity,
} from '~/components/exposures/PaymentRequestContainer/MakePaymentRequestDialog/AnomalousPaymentBanner';
import useFetchReservePaymentPossibility from '~/components/exposures/PaymentRequestContainer/useFetchReservePaymentPossibility';
import useFetchExposure from '~/components/exposures/useFetchExposure';
import { useHasPermission } from '~/components/hooks/useHasPermission';
import { useFetchClaim } from '~/hooks/useFetchClaim';

import { CONFIGURATION_FEATURES_NAMES, SUPERVISOR_APPROVAL_STATUS_DICT } from '../../Types';
import { capitalize, isFeatureEnabled, reportAxiosError } from '../../Utils';
import CardDialog from '../CardDialog';
import CheckboxFormik from '../CheckboxFormik';
import { ClaimContextProvider } from '../ClaimContainer';
import ClaimLink from '../ClaimLink';
import { ContactShowOnlyTextField } from '../ContactTextFieldFormik';
import { LoadingSwitch, PERMISSION_ACTIONS, PERMISSION_VERBS, Text } from '../core';
import { useCurrencyFormatter } from '../CurrencyFormatterContext';
import { useCms } from '../hooks/useCms';
import { usePaymentsLimits } from '../hooks/usePaymentLimits';
import { useExposureSubReserves } from '../hooks/useSubReserves';
import LoadingIndicator from '../LoadingIndicator';
import useOrganization from '../OrganizationContext';
import { TextFieldFormik } from '../TextFieldFormik';
import ToggleButtonGroupFormik from '../ToggleButtonGroupFormik';

import {
  doesLimitCoverAmount,
  getPayableWithReserveType,
  getPolicyExposureLimit,
  isLimitExists,
} from './ExposureUtils';
import RequestAmountFormik from './RequestAmountFormik';

import { useStyles } from '../../assets/styles';

const getTitle = (payableType) => {
  const titlePayableType = payableType === 'expenses' ? 'Expenses' : 'Indemnity';
  return `Update ${titlePayableType} Reserve`;
};

function ReserveChangeRequestContainer(props) {
  const { user, userOrganization } = useCms();
  const { subReserves, isDelegatedAuthorityEnabled } = useOrganization();

  const {
    claim,
    payableWithReserve,
    exposure,
    reserveChangeRequest,
    cardDialogProps,
    onClose,
    onUpdate,
    AdditionalDialogHeader,
    refreshInBackground = false,
  } = props;
  const { reserve, was_payment_request_issued } = payableWithReserve;

  const policyExposureLimit = getPolicyExposureLimit(claim, exposure);
  const payableType = getPayableWithReserveType(exposure, payableWithReserve);

  const canApproveAsManager = user.is_org_level_supervisor || user.is_manager;
  // Under delegated authority, approving is level-based: fetch the user's remaining
  // authority (the /limit endpoint is delegated-authority-aware) and let a covering
  // limit open the approval card even for non-managers. Skipped for the requester —
  // they can never decide their own request.
  const shouldCheckDelegatedAuthorityCoverage = Boolean(
    isDelegatedAuthorityEnabled &&
      reserveChangeRequest &&
      reserveChangeRequest.request_status === 'pending' &&
      !canApproveAsManager &&
      user.id !== reserveChangeRequest.requester_user?.id
  );
  const { userExposureLimit, isUserExposureLimitError } = usePaymentsLimits(
    claim,
    exposure,
    payableType,
    'reserve',
    shouldCheckDelegatedAuthorityCoverage
  );
  const hasCoveringDelegatedAuthority =
    shouldCheckDelegatedAuthorityCoverage &&
    !isNil(userExposureLimit) &&
    doesLimitCoverAmount(userExposureLimit, reserveChangeRequest.request_amount);

  const shouldDisplaySubReserves =
    isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.SUB_RESERVERS_IN_PD_AND_BI) &&
    subReserves[exposure.coverage_type];
  const subReservesForExposure = shouldDisplaySubReserves ? subReserves[exposure.coverage_type] : undefined;

  async function _handleSubmit(path, values, { backgroundRefresh = false } = {}) {
    let res;
    try {
      res = await axios.post(path, values);
    } catch (error) {
      reportAxiosError(error);
      throw error;
    }

    if (backgroundRefresh) {
      // Close the dialog as soon as the request succeeds and let the claim/list refresh run
      // asynchronously in the background, instead of blocking the dialog open until the
      // (potentially heavy) refresh resolves. A failed refresh is still reported so the user
      // knows the view may be stale.
      onClose();
      Promise.resolve(onUpdate(res)).catch(reportAxiosError);
      return;
    }

    try {
      await onUpdate(res);
    } catch (error) {
      reportAxiosError(error);
      throw error;
    }
    onClose();
  }

  const payableUrl = exposure.isGeneralExpenses
    ? `/api/v1/claims/${claim.id}/general_expenses/reserve_change_requests`
    : `/api/v1/claims/${claim.id}/exposures/${exposure.id}/${payableType}/reserve_change_requests`;

  function handleSubmitReserve(values) {
    return _handleSubmit(payableUrl, values);
  }

  function handleCancelReserveChangeRequest(values) {
    return _handleSubmit(
      `${payableUrl}/${reserveChangeRequest.id}/decide`,
      { ...values, decision: 'cancel' },
      { backgroundRefresh: refreshInBackground }
    );
  }

  function handleApproveReserveChangeRequest(values) {
    return _handleSubmit(`${payableUrl}/${reserveChangeRequest.id}/decide`, values, {
      backgroundRefresh: refreshInBackground,
    });
  }

  function getCardDialog() {
    if (!reserveChangeRequest) {
      return (
        <ReserveChangeRequestCard
          cardDialogProps={cardDialogProps}
          onSubmitReserve={handleSubmitReserve}
          payableWithReserve={payableWithReserve}
          payableType={payableType}
          onCancel={onClose}
          policyExposureLimit={policyExposureLimit}
          exposure={exposure}
          claim={claim}
          legacySubReserves={subReservesForExposure}
          AdditionalDialogHeader={AdditionalDialogHeader}
        />
      );
    }

    // reserve_change_request
    if (reserveChangeRequest.request_status !== 'pending') {
      return (
        <ReserveChangeRequestDecisionCard
          cardDialogProps={cardDialogProps}
          reserveChangeRequest={reserveChangeRequest}
          claim={claim}
          exposure={exposure}
          payableType={payableType}
          onClose={onClose}
          legacySubReserves={subReservesForExposure}
          wasPaymentIssued={was_payment_request_issued}
          AdditionalDialogHeader={AdditionalDialogHeader}
        />
      );
    }

    // reserve_change_request is pending
    if (shouldCheckDelegatedAuthorityCoverage && isNil(userExposureLimit) && !isUserExposureLimitError) {
      // The remaining-authority fetch decides which card to render; rendering the
      // cancel-only card while it is in flight would flash the wrong card. If the
      // fetch failed, fall through with no coverage so the cancel card still renders.
      return null;
    }

    if ((canApproveAsManager || hasCoveringDelegatedAuthority) && user.id !== reserveChangeRequest.requester_user?.id) {
      return (
        <ReserveChangeApprovalCard
          cardDialogProps={cardDialogProps}
          reserveChangeRequest={reserveChangeRequest}
          currentReserve={reserve}
          onDecideReserveChangeRequest={handleApproveReserveChangeRequest}
          onCancel={onClose}
          payableWithReserve={payableWithReserve}
          policyExposureLimit={policyExposureLimit}
          claim={claim}
          exposure={exposure}
          payableType={payableType}
          legacySubReserves={subReservesForExposure}
          AdditionalDialogHeader={AdditionalDialogHeader}
        />
      );
    } else {
      return (
        <ReserveChangeCancelCard
          cardDialogProps={cardDialogProps}
          reserveChangeRequest={reserveChangeRequest}
          currentReserve={reserve}
          onCancelReserveChangeRequest={handleCancelReserveChangeRequest}
          onCancel={onClose}
          legacySubReserves={subReservesForExposure}
          wasPaymentIssued={was_payment_request_issued}
          payableType={payableType}
          exposure={exposure}
          claim={claim}
          AdditionalDialogHeader={AdditionalDialogHeader}
        />
      );
    }
  }

  return <>{getCardDialog()}</>;
}

ReserveChangeRequestContainer.propTypes = {
  claim: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired,
  payableWithReserve: PropTypes.object.isRequired,
  exposure: PropTypes.object.isRequired,
  reserveChangeRequest: PropTypes.object,
  cardDialogProps: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  AdditionalDialogHeader: PropTypes.node,
  refreshInBackground: PropTypes.bool,
};

function ReserveChangeRequestDynamicContainer({
  claimId,
  exposureId,
  payableWithReserveId,
  reserveChangeRequest,
  cardDialogProps,
  onClose,
  onUpdate,
  AdditionalDialogHeader = <React.Fragment />,
  refreshInBackground = false,
}) {
  const [claim, isLoadingClaim, isErrorClaim, reloadClaim] = useFetchClaim(claimId);
  const {
    isLoadingExposure,
    isErrorExposure,
    exposure: exposureFetchedOrUndefined,
  } = useFetchExposure(claimId, exposureId); // exposureId might be undefined

  const { userOrganization } = useCms();

  const isEfficientExposures = isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.CONFIGURABLE_FNOL);

  const isLoading = isLoadingClaim || isLoadingExposure;
  const isError = isErrorClaim || isErrorExposure;

  if (isLoading) {
    return (
      <CardDialog title="Update Reserve" {...cardDialogProps}>
        <LoadingIndicator isError={isError} />
      </CardDialog>
    );
  }

  let payableWithReserve = null;
  let exposure = null;
  if (claim.general_expenses.id === payableWithReserveId) {
    payableWithReserve = claim.general_expenses;
    // TODO: Move this to somewhere else
    exposure = {
      isGeneralExpenses: true,
      is_closed: false,
      expenses: claim.general_expenses,
    };
  } else {
    exposure = isEfficientExposures
      ? exposureFetchedOrUndefined
      : claim.exposures.find((exposure) => exposure.id === exposureId);
    if (!exposure) {
      throw Error(`Couldn't find exposure ${exposureId} in claim ${claimId}`);
    }

    if (exposure.indemnity.id === payableWithReserveId) {
      payableWithReserve = exposure.indemnity;
    } else if (exposure.expenses.id === payableWithReserveId) {
      payableWithReserve = exposure.expenses;
    } else {
      throw Error(`Couldn't find payableWithReserve ${payableWithReserveId} in exposure ${exposureId}`);
    }
  }

  return (
    <ClaimContextProvider claim={claim} refreshData={reloadClaim}>
      <ReserveChangeRequestContainer
        claim={claim}
        exposure={exposure}
        payableWithReserve={payableWithReserve}
        reserveChangeRequest={reserveChangeRequest}
        cardDialogProps={cardDialogProps}
        onClose={onClose}
        onUpdate={onUpdate}
        AdditionalDialogHeader={AdditionalDialogHeader}
        refreshInBackground={refreshInBackground}
      />
    </ClaimContextProvider>
  );
}

ReserveChangeRequestDynamicContainer.propTypes = {
  claimId: PropTypes.number.isRequired,
  exposureId: PropTypes.number.isRequired,
  payableWithReserveId: PropTypes.number.isRequired,
  onUpdate: PropTypes.func.isRequired,
  reserveChangeRequest: PropTypes.object,
  cardDialogProps: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  AdditionalDialogHeader: PropTypes.node,
  refreshInBackground: PropTypes.bool,
};

// Component to track form values and update parent state
const FormValueTracker = ({ values, onReserveAmountChange }) => {
  const reserveValue = values['reserve'];

  React.useEffect(() => {
    // Always call onReserveAmountChange, even for 0 values
    // Previous bug: if (reserveValue) would skip 0 values since 0 is falsy
    if (reserveValue !== undefined && reserveValue !== null) {
      onReserveAmountChange(reserveValue);
    }
  }, [reserveValue, onReserveAmountChange]);

  return null;
};

FormValueTracker.propTypes = {
  values: PropTypes.object.isRequired,
  onReserveAmountChange: PropTypes.func.isRequired,
};

function ReserveChangeRequestCard(props) {
  const {
    claim,
    onSubmitReserve,
    cardDialogProps,
    onCancel,
    payableWithReserve,
    policyExposureLimit,
    payableType,
    legacySubReserves,
    exposure,
    AdditionalDialogHeader,
  } = props;
  const { classes } = useStyles();
  const { userOrganization } = useCms();

  const [showPolicyExposureLimitDialog, setShowPolicyExposureLimitDialog] = React.useState(false);
  const [showReserveVerificationDialog, setShowReserveVerificationDialog] = React.useState(false);
  const [currentReserveAmount, setCurrentReserveAmount] = React.useState(payableWithReserve?.reserve ?? 0);

  // Update when payableWithReserve changes
  React.useEffect(() => {
    setCurrentReserveAmount(payableWithReserve?.reserve ?? 0);
  }, [payableWithReserve?.reserve]);

  // Callback to update currentReserveAmount from form changes
  const handleReserveAmountChange = React.useCallback((newAmount) => {
    setCurrentReserveAmount(newAmount);
  }, []);
  const { isSubReservesConfigEnabled, subReservesConfigMap } = useExposureSubReserves(payableType, exposure);
  const { userExposureLimit, isUserExposureLimitLoading } = usePaymentsLimits(
    claim,
    exposure,
    payableType,
    'reserve',
    cardDialogProps.open ?? true
  );

  // Fetch payment possibility for reserve validation (only if feature flag is enabled)
  const isReserveWarningsEnabled = isFeatureEnabled(
    userOrganization,
    CONFIGURATION_FEATURES_NAMES.RESERVE_CHANGE_REQUEST_WARNINGS
  );
  const { reservePolicyLimitBreaches, isReservePaymentIssuePreventingReserve } = useFetchReservePaymentPossibility(
    claim.id,
    exposure?.id ?? null,
    payableType,
    isReserveWarningsEnabled ? currentReserveAmount : 0 // Pass 0 to disable when feature flag is off
  );

  const getReserveChangeRequestFields = (payableWithReserve) => {
    const fields = {
      reserve: payableWithReserve?.reserve ?? '',
      note: '',
      financial_request_extra: {
        sub_reserves: {},
      },
    };

    if (payableWithReserve.legacy_last_reserve_change_request_sub_reserves) {
      fields.financial_request_extra.sub_reserves = payableWithReserve.legacy_last_reserve_change_request_sub_reserves;
    }

    if (isSubReservesConfigEnabled) {
      const subReserveKeys = [...subReservesConfigMap.keys()];
      const initialSubReserves = chain(subReserveKeys)
        .keyBy((key) => key)
        .mapValues(() => 0)
        .value();
      fields.sub_reserves = { ...initialSubReserves, ...(payableWithReserve?.sub_reserves ?? {}) };
    }

    return fields;
  };

  const getReserveChangeRequestValidationSchema = () => {
    const reserveChangeRequestValidationFields = {
      reserve: Yup.number().required('Required'),
    };

    if (isSubReservesConfigEnabled) {
      const subReserveKeys = [...subReservesConfigMap.keys()];
      const subReservesValidationSchema = chain(subReserveKeys)
        .keyBy((key) => key)
        .mapValues(() => Yup.number().required('Required'))
        .value();
      reserveChangeRequestValidationFields.sub_reserves = Yup.object().shape(subReservesValidationSchema);
    }

    return Yup.object().shape(reserveChangeRequestValidationFields);
  };

  // Helper function to get anomalous reserve banner props
  const getAnomalousReserveBannerProps = (checkIsExceedsUserAuthority) => {
    const issuesAndSeverities = [];

    if (checkIsExceedsUserAuthority()) {
      issuesAndSeverities.push({
        issue: 'Reserve exceeds authority level.',
        severity: IssueSeverity.WARNING,
      });
    }

    if (reservePolicyLimitBreaches) {
      reservePolicyLimitBreaches.forEach((plb) => {
        issuesAndSeverities.push({
          issue: plb.reason,
          severity: isReservePaymentIssuePreventingReserve ? IssueSeverity.ERROR : IssueSeverity.WARNING,
        });
      });
    }

    return {
      issuesAndSeverities,
    };
  };

  return (
    <Formik
      initialValues={getReserveChangeRequestFields(payableWithReserve)}
      validationSchema={getReserveChangeRequestValidationSchema()}
      enableReinitialize
      onSubmit={(values, formikProps) => {
        if (
          payableType !== 'expenses' &&
          isLimitExists(policyExposureLimit) &&
          values['reserve'] + payableWithReserve.paid_sum > policyExposureLimit
        ) {
          setShowPolicyExposureLimitDialog(true);
          formikProps.setSubmitting(false);
          return;
        }

        // Check if there are reserve payment possibility issues that require verification (only if feature flag is enabled)
        if (isReserveWarningsEnabled && !isEmpty(reservePolicyLimitBreaches)) {
          setShowReserveVerificationDialog(true);
          formikProps.setSubmitting(false);
          return;
        }

        onSubmitReserve(values)
          .then(() => formikProps.resetForm())
          .catch(() => {
            formikProps.setSubmitting(false);
          });
      }}
    >
      {(formikProps) => {
        const { isSubmitting, handleSubmit, values } = formikProps;

        const checkIsExceedsUserAuthority = () =>
          isLimitExists(userExposureLimit) && userExposureLimit < values['reserve'];

        return (
          <>
            <FormValueTracker values={values} onReserveAmountChange={handleReserveAmountChange} />
            <CardDialog
              title={getTitle(payableType)}
              width="850px"
              {...cardDialogProps}
              onClose={() => {
                formikProps.handleReset();
                onCancel();
              }}
            >
              <LoadingSwitch isLoading={isUserExposureLimitLoading}>
                {AdditionalDialogHeader}
                {isReserveWarningsEnabled && (
                  <AnomalousPaymentBanner
                    {...getAnomalousReserveBannerProps(checkIsExceedsUserAuthority)}
                    type="reserve"
                  />
                )}
                <RequestAmountFormik
                  id="reserve"
                  label="Reserve"
                  labelWithSubReserves="Reserve total"
                  isSubmitting={isSubmitting}
                  isExceedingLimit={checkIsExceedsUserAuthority()}
                  payableType={payableType}
                  legacySubReserves={legacySubReserves}
                  wasPaymentIssued={payableWithReserve.was_payment_request_issued}
                  exposure={exposure}
                  claim={claim}
                />
                <div className={classes.inputContainer}>
                  <TextFieldFormik
                    id="note"
                    label="Reason"
                    rows={2}
                    className={classes.textField}
                    fullWidth
                    multiline
                  />
                </div>
                <div className={classes.buttonsContainer}>
                  <Button variant="contained" color="primary" disabled={isSubmitting} onClick={handleSubmit}>
                    {checkIsExceedsUserAuthority() ? 'Request Supervisor Approval' : 'Update'}
                  </Button>
                </div>
              </LoadingSwitch>
            </CardDialog>
            {showPolicyExposureLimitDialog && (
              <ReserveExceedsPolicyLimitsCard
                open={showPolicyExposureLimitDialog}
                claim={claim}
                onClose={() => setShowPolicyExposureLimitDialog(false)}
                onOverride={async (overrideValues) => {
                  await onSubmitReserve({ ...values, ...overrideValues });
                  formikProps.resetForm();
                  setShowPolicyExposureLimitDialog(false);
                }}
              />
            )}
            {isReserveWarningsEnabled && showReserveVerificationDialog && (
              <AdditionalReserveVerificationDialog
                preSubmitCheckTitle={
                  !isEmpty(reservePolicyLimitBreaches)
                    ? reservePolicyLimitBreaches
                        .map((breach) => breach.reason?.replace('Payment', 'Reserve'))
                        .join(', ')
                    : 'Reserve validation issues found'
                }
                onClose={() => setShowReserveVerificationDialog(false)}
                onProceed={async (overrideDetails) => {
                  // Proceed with reserve update with override details
                  await onSubmitReserve({
                    ...values,
                    additional_verifications_extra: { override_details: overrideDetails },
                  });
                  formikProps.resetForm();
                  setShowReserveVerificationDialog(false);
                }}
                disabled={isSubmitting}
              />
            )}
          </>
        );
      }}
    </Formik>
  );
}

ReserveChangeRequestCard.propTypes = {
  claim: PropTypes.object.isRequired,
  cardDialogProps: PropTypes.object.isRequired,
  onSubmitReserve: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  payableWithReserve: PropTypes.object.isRequired,
  payableType: PropTypes.string.isRequired,
  policyExposureLimit: PropTypes.number.isRequired,
  exposure: PropTypes.object,
  legacySubReserves: PropTypes.arrayOf(PropTypes.string),
  AdditionalDialogHeader: PropTypes.node,
};

function ReserveExceedsPolicyLimitsCard(props) {
  const { open, onClose, onOverride } = props;
  const { classes } = useStyles();

  const userHasClaimPermissions = useHasPermission({
    action: PERMISSION_ACTIONS.APPROVE_CHANGE_INDEMNITY_RESERVE_OVER_LIMIT,
    verb: PERMISSION_VERBS.WRITE,
  });

  return (
    <CardDialog title="Reserve exceeds policy limits" isDialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <Typography display="block">Reserve exceeds policy limits</Typography>
      {userHasClaimPermissions && onOverride && (
        <Formik
          initialValues={{
            is_override_limit: false,
            override_limit_reason: '',
          }}
          validationSchema={Yup.object({
            is_override_limit: Yup.bool(),
            override_limit_reason: Yup.string().required('Required'),
          })}
          enableReinitialize
          onSubmit={(values, formikProps) => {
            try {
              onOverride(values);
            } catch (exception) {
              formikProps.setSubmitting(false);
            }
          }}
        >
          {(formikProps) => {
            const { values, handleSubmit, isSubmitting } = formikProps;
            return (
              <div style={{ marginTop: '16px' }}>
                <Grid container>
                  <Grid item xs={12}>
                    <CheckboxFormik
                      id="is_override_limit"
                      label="Set reserve over policy limit"
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextFieldFormik
                      id="override_limit_reason"
                      label="Reason"
                      className={classes.textField}
                      fullWidth
                      disabled={isSubmitting || !values.is_override_limit}
                    />
                  </Grid>
                  <div className={classes.buttonsContainer}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSubmit}
                      disabled={isSubmitting || !values.is_override_limit}
                    >
                      Confirm
                    </Button>
                  </div>
                </Grid>
              </div>
            );
          }}
        </Formik>
      )}
    </CardDialog>
  );
}

ReserveExceedsPolicyLimitsCard.propTypes = {
  claim: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onOverride: PropTypes.func,
};

function ReserveChangeCancelCard(props) {
  const {
    reserveChangeRequest,
    currentReserve,
    onCancelReserveChangeRequest,
    cardDialogProps,
    onCancel,
    legacySubReserves,
    wasPaymentIssued,
    payableType,
    exposure,
    claim,
    AdditionalDialogHeader,
  } = props;
  const { classes } = useStyles();

  return (
    <Formik
      initialValues={{
        ...reserveChangeRequest,
        note: '',
      }}
      validationSchema={Yup.object().shape({ note: Yup.string().required('Required') })}
      enableReinitialize
      onSubmit={(values, formikProps) => {
        onCancelReserveChangeRequest(values)
          .then(() => formikProps.resetForm())
          .catch(() => {
            formikProps.setSubmitting(false);
          });
      }}
    >
      {(formikProps) => {
        const { isSubmitting, handleSubmit } = formikProps;

        return (
          <CardDialog
            title={getTitle(payableType)}
            width="450px"
            {...cardDialogProps}
            onClose={() => {
              formikProps.handleReset();
              onCancel();
            }}
          >
            {AdditionalDialogHeader}
            <Grid container>
              <Grid item>
                <RequestAmountFormik
                  id="request_amount"
                  label="Reserve"
                  labelWithSubReserves="Reserve total"
                  isSubmitting={isSubmitting}
                  payableType={payableType}
                  legacySubReserves={legacySubReserves}
                  showOnly
                  wasPaymentIssued={wasPaymentIssued}
                  pendingMessage="Pending approval"
                  exposure={exposure}
                  claim={claim}
                >
                  <ExposureMonetaryValueTextFieldFormik
                    id="current_reserve"
                    label="Current reserve"
                    className={classes.textField}
                    value={currentReserve}
                    showOnly
                    exposure={exposure}
                    claim={claim}
                  />
                </RequestAmountFormik>
              </Grid>
              <Grid item xs={6} />
            </Grid>
            <div>
              <br />
            </div>
            <div className={classes.inputContainer}>
              <TextFieldFormik
                id="request_note"
                label="Adjuster's original note"
                rows={2}
                className={classes.textField}
                fullWidth
                multiline
                showOnly
              />
            </div>
            <div>
              <br />
            </div>
            <div className={classes.inputContainer}>
              <TextFieldFormik id="note" label="Note" rows={2} className={classes.textField} fullWidth multiline />
            </div>
            <div className={classes.buttonsContainer}>
              <Button variant="contained" color="primary" disabled={isSubmitting} onClick={handleSubmit}>
                Cancel Request
              </Button>
            </div>
          </CardDialog>
        );
      }}
    </Formik>
  );
}

ReserveChangeCancelCard.propTypes = {
  cardDialogProps: PropTypes.object.isRequired,
  reserveChangeRequest: PropTypes.object.isRequired,
  currentReserve: PropTypes.number.isRequired,
  onCancelReserveChangeRequest: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  legacySubReserves: PropTypes.arrayOf(PropTypes.string),
  wasPaymentIssued: PropTypes.bool,
  payableType: PropTypes.string.isRequired,
  exposure: PropTypes.object,
  claim: PropTypes.object.isRequired,
  AdditionalDialogHeader: PropTypes.node,
};

function ReserveChangeApprovalCard(props) {
  const {
    reserveChangeRequest,
    currentReserve,
    onDecideReserveChangeRequest,
    cardDialogProps,
    onCancel,
    payableWithReserve,
    policyExposureLimit,
    claim,
    exposure,
    payableType,
    legacySubReserves,
    AdditionalDialogHeader,
  } = props;
  const { classes } = useStyles();
  const { currencyFormatter } = useCurrencyFormatter();
  const { isSubReservesConfigEnabled } = useExposureSubReserves(payableType, exposure);

  const [showPreSubmitCheckResultsDialog, setShowPreSubmitCheckResultsDialog] = React.useState(false);
  const { userExposureLimit, isUserExposureLimitLoading } = usePaymentsLimits(
    claim,
    exposure,
    payableType,
    'reserve',
    cardDialogProps.open ?? true
  );

  return (
    <Formik
      initialValues={{
        ...reserveChangeRequest,
        note: '',
        decision: '',
        decision_amount: '',
      }}
      validationSchema={Yup.object().shape({
        note: Yup.string().required('Required'),
        decision: Yup.string().required('Required').oneOf(['approve', 'approve_different', 'decline']),
        decision_amount: Yup.number().when('decision', {
          is: 'approve_different',
          then: Yup.number().required('Required'),
        }),
      })}
      enableReinitialize
      onSubmit={(values, formikProps) => {
        if (
          payableType !== 'expenses' &&
          isLimitExists(policyExposureLimit) &&
          values['decision_amount'] + payableWithReserve.paid_sum > policyExposureLimit &&
          !get(values, 'additional_verifications_extra.override_details.is_override_limit', false)
        ) {
          setShowPreSubmitCheckResultsDialog(true);
          formikProps.setSubmitting(false);
          return;
        }

        onDecideReserveChangeRequest(values)
          .then(() => formikProps.resetForm())
          .catch(() => {
            formikProps.setSubmitting(false);
          });
      }}
    >
      {(formikProps) => {
        const { isSubmitting, handleSubmit, values, setFieldValue } = formikProps;
        const decision = values.decision;
        let approvingAmount = 0;
        if (decision === 'approve') {
          approvingAmount = reserveChangeRequest.request_amount;
        } else if (decision === 'approve_different') {
          approvingAmount = values.decision_amount;
        }

        const isExceedsUserAuthority = () => isLimitExists(userExposureLimit) && userExposureLimit < approvingAmount;

        function switchAmountField() {
          switch (values['decision']) {
            case 'approve':
              return (
                <RequestAmountFormik
                  id="request_amount"
                  label="Approved reserve"
                  labelWithSubReserves="Approved reserve total"
                  isSubmitting={isSubmitting}
                  payableType={payableType}
                  legacySubReserves={legacySubReserves}
                  isExceedingLimit={isExceedsUserAuthority()}
                  showOnly
                  wasPaymentIssued={payableWithReserve.was_payment_request_issued}
                  exposure={exposure}
                  claim={claim}
                />
              );
            case 'approve_different':
              return (
                <RequestAmountFormik
                  id="decision_amount"
                  label="New reserve"
                  labelWithSubReserves="New reserve total"
                  isSubmitting={isSubmitting}
                  payableType={payableType}
                  legacySubReserves={legacySubReserves}
                  isExceedingLimit={isExceedsUserAuthority()}
                  wasPaymentIssued={payableWithReserve.was_payment_request_issued}
                  exposure={exposure}
                  claim={claim}
                />
              );
            case 'decline':
              return (
                <ExposureMonetaryValueTextFieldFormik
                  key="decline"
                  id="request_amount"
                  label="New reserve"
                  value={currentReserve}
                  showOnly
                  exposure={exposure}
                  claim={claim}
                />
              );
            default:
              return (
                <div style={{ visibility: 'hidden' }}>
                  <ExposureMonetaryValueTextFieldFormik
                    key="placeholder"
                    id="request_amount"
                    label="New reserve"
                    value={currentReserve}
                    showOnly
                    exposure={exposure}
                    claim={claim}
                  />
                </div>
              );
          }
        }

        function switchSubmitButton() {
          let buttonName = '';

          if (!values['decision']) {
            return (
              <Button disabled variant="contained" color="primary">
                Approve Request
              </Button>
            );
          }

          switch (values['decision']) {
            case 'approve':
              buttonName = isExceedsUserAuthority() ? 'Request Supervisor Approval' : 'Approve Request';
              break;
            case 'approve_different':
              buttonName = isExceedsUserAuthority() ? 'Request Supervisor Approval' : 'Approve Different Request';
              break;
            case 'decline':
              buttonName = 'Decline Request';
              break;
            default:
              throw Error(`Unknown value for decision: ${values['decision']}`);
          }

          return (
            <Button variant="contained" color="primary" disabled={isSubmitting} onClick={handleSubmit}>
              {buttonName}
            </Button>
          );
        }

        const requestActionsOptions = [
          {
            value: 'approve',
            key: 'approve',
            label: (
              <Text weight={Text.WEIGHTS.REGULAR} variant={Text.VARIANTS.SM}>
                Approve
              </Text>
            ),
          },
          {
            value: 'decline',
            key: 'decline',
            label: (
              <Text weight={Text.WEIGHTS.REGULAR} variant={Text.VARIANTS.SM}>
                Decline
              </Text>
            ),
          },
        ];

        if (!isSubReservesConfigEnabled) {
          requestActionsOptions.splice(1, 0, {
            value: 'approve_different',
            key: 'approve_different',
            label: (
              <Text weight={Text.WEIGHTS.REGULAR} variant={Text.VARIANTS.SM}>
                Approve different sum
              </Text>
            ),
          });
        }

        return (
          <>
            <CardDialog
              title={`${capitalize(payableType)} Reserve Change Approval`}
              width="450px"
              {...cardDialogProps}
              onClose={() => {
                formikProps.handleReset();
                onCancel();
              }}
            >
              <LoadingSwitch isLoading={isUserExposureLimitLoading}>
                {AdditionalDialogHeader}
                <Typography>{`Requested by: ${reserveChangeRequest.requester_user.username}`}</Typography>
                <div className={classes.inputContainer}>
                  <ClaimLink claimId={claim.id} linkText={claim.claim_id_display} />
                  {claim.policy.insured_contact && (
                    // In self insured organization there is no insured
                    <ContactShowOnlyTextField
                      contactId={claim.policy.insured_contact.id}
                      contactDisplayName={claim.policy.insured_contact.full_name}
                      label="Insured"
                    />
                  )}
                </div>
                <div className={classes.inputContainer}>
                  {exposure.isGeneralExpenses ? 'General Expenses' : <ExposureDetailedTitle exposure={exposure} />}
                </div>
                <Grid container>
                  <Grid item>
                    <ExposureMonetaryValueTextFieldFormik
                      id="request_amount"
                      label="Reserve"
                      className={classes.textField}
                      showOnly
                      exposure={exposure}
                      claim={claim}
                    />
                  </Grid>
                  <Grid item xs={6} />
                  <Grid item>
                    <ExposureMonetaryValueTextFieldFormik
                      id="current_reserve"
                      label="Current reserve"
                      className={classes.textField}
                      value={currentReserve}
                      showOnly
                      exposure={exposure}
                      claim={claim}
                    />
                  </Grid>
                </Grid>
                <div className={classes.inputContainer}>
                  <TextFieldFormik
                    id="request_note"
                    label="Adjuster's original note"
                    rows={2}
                    className={classes.textField}
                    fullWidth
                    multiline
                    showOnly
                  />
                </div>
                <ApprovalRequests approvalRequests={reserveChangeRequest?.approval_requests} className="mb-16" />

                <ToggleButtonGroupFormik id="decision" options={requestActionsOptions} />

                {switchAmountField()}
                <div className={classes.inputContainer}>
                  <TextFieldFormik id="note" label="Note" rows={2} className={classes.textField} fullWidth multiline />
                </div>
                <div className={classes.buttonsContainer}>{switchSubmitButton()}</div>
              </LoadingSwitch>
            </CardDialog>
            {showPreSubmitCheckResultsDialog && (
              <AdditionalReserveVerificationDialog
                onClose={() => setShowPreSubmitCheckResultsDialog(false)}
                isSubmitting={isSubmitting}
                values={values}
                disabled={isSubmitting}
                preSubmitCheckTitle={`New incurred of ${currencyFormatter.format(
                  payableWithReserve.paid_sum + values['decision_amount']
                )} will exceed limit of ${currencyFormatter.format(policyExposureLimit)}`}
                onProceed={async (overrideDetails) => {
                  setFieldValue('additional_verifications_extra', {
                    results: [
                      `New incurred of ${currencyFormatter.format(
                        payableWithReserve.paid_sum + values['decision_amount']
                      )} will exceed limit of ${currencyFormatter.format(policyExposureLimit)}`,
                    ],
                    override_details: overrideDetails,
                  });
                  await handleSubmit();
                }}
              />
            )}
          </>
        );
      }}
    </Formik>
  );
}

ReserveChangeApprovalCard.propTypes = {
  cardDialogProps: PropTypes.object.isRequired,
  reserveChangeRequest: PropTypes.object.isRequired,
  currentReserve: PropTypes.number.isRequired,
  onDecideReserveChangeRequest: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  payableWithReserve: PropTypes.object.isRequired,
  policyExposureLimit: PropTypes.number.isRequired,
  claim: PropTypes.object.isRequired,
  exposure: PropTypes.object.isRequired,
  payableType: PropTypes.string.isRequired,
  legacySubReserves: PropTypes.arrayOf(PropTypes.string),
  AdditionalDialogHeader: PropTypes.node,
};

function ReserveChangeRequestDecisionCard(props) {
  const {
    reserveChangeRequest,
    claim,
    exposure,
    payableType,
    cardDialogProps,
    onClose,
    legacySubReserves,
    wasPaymentIssued,
    AdditionalDialogHeader,
  } = props;
  const { classes } = useStyles();

  return (
    <Formik
      initialValues={{
        ...reserveChangeRequest,
      }}
      enableReinitialize
      onSubmit={() => {}}
    >
      {(formikProps) => {
        const { values, isSubmitting } = formikProps;

        return (
          <CardDialog
            title={`${capitalize(payableType)} Reserve Change Decision`}
            width="450px"
            {...cardDialogProps}
            onClose={onClose}
          >
            {AdditionalDialogHeader}
            <Typography>{`Decision made by: ${reserveChangeRequest.decider_user.username}`}</Typography>
            <div className={classes.inputContainer}>
              <ClaimLink claimId={claim.id} linkText={claim.claim_id_display} />
              {claim.policy.insured_contact && (
                <ContactShowOnlyTextField
                  contactId={claim.policy.insured_contact.id}
                  contactDisplayName={claim.policy.insured_contact.full_name}
                  label="Insured"
                />
              )}
            </div>
            <div className={classes.inputContainer}>
              {exposure.isGeneralExpenses ? 'General Expenses' : <ExposureDetailedTitle exposure={exposure} />}
            </div>
            <RequestAmountFormik
              id="request_amount"
              label="Reserve Requested"
              labelWithSubReserves="Reserve total requested"
              isSubmitting={isSubmitting}
              payableType={payableType}
              legacySubReserves={legacySubReserves}
              wasPaymentIssued={wasPaymentIssued}
              showOnly
              exposure={exposure}
              claim={claim}
            />
            <div className={classes.inputContainer}>
              <TextFieldFormik
                id="request_note"
                label="Adjuster's original note"
                rows={2}
                className={classes.textField}
                fullWidth
                multiline
                showOnly
              />
            </div>
            <div>
              <br />
            </div>
            <div>
              <Typography display="block">{`Decision: ${
                SUPERVISOR_APPROVAL_STATUS_DICT[values['request_status']].desc
              }`}</Typography>
            </div>
            <div>
              <ExposureMonetaryValueTextFieldFormik
                id="decision_amount"
                label="Reserve Approved"
                className={classes.textField}
                showOnly
                exposure={exposure}
                claim={claim}
              />
            </div>
            <div className={classes.inputContainer}>
              <TextFieldFormik
                id="decision_note"
                label="Decision Note"
                rows={2}
                className={classes.textField}
                fullWidth
                multiline
                showOnly
              />
            </div>
          </CardDialog>
        );
      }}
    </Formik>
  );
}

ReserveChangeRequestDecisionCard.propTypes = {
  cardDialogProps: PropTypes.object.isRequired,
  reserveChangeRequest: PropTypes.object.isRequired,
  claim: PropTypes.object.isRequired,
  exposure: PropTypes.object.isRequired,
  payableType: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  legacySubReserves: PropTypes.arrayOf(PropTypes.string),
  wasPaymentIssued: PropTypes.bool,
  AdditionalDialogHeader: PropTypes.node,
};

export default ReserveChangeRequestContainer;
export { ReserveChangeRequestDynamicContainer };
